import axios from "axios";

import { config } from "shared/data";

/**
 * Creates a URL for loading game content in an iframe
 *
 * @param path - The game path to append after the host (default: "games")
 * @param params - Query parameters to include
 * @returns Full game URL with platform parameter
 */
export const createGameUrl = (path = "games", parameters: Record<string, string> = {}): string => {
  const url = new URL(`${path}`, config.game.host);

  url.searchParams.set("platform", "pwa");

  Object.entries(parameters).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
};

export type CheckCrosswordDateResponse = {
  gameTitle?: null | string;
  isValidGame: boolean;
};

/**
 * Check a crossword game date and checks if the game exists
 *
 * @param date - The date string to valid
 * @returns Object with isValidGame boolean and optional gameTitle
 */
export const checkCrosswordDate = async (
  date: string,
  gameType: string,
): Promise<CheckCrosswordDateResponse> => {
  if (!date || typeof date !== "string" || (gameType !== "cryptic" && gameType !== "quick")) {
    return {
      isValidGame: false,
    };
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) {
    return {
      isValidGame: false,
    };
  }

  try {
    // Check if game exists for this date
    const response = await axios.get(
      `${config.game.api.host}/public/game-asset/crossword_${gameType}_${date}`,
      {
        headers: {
          apiKey: config.game.api.apiKey,
        },
      },
    );

    if (response.status !== 200) {
      console.error(`Failed to fetch crossword data for ${date}: status ${response.status}`);
      return {
        isValidGame: false,
      };
    }

    const responseData = response.data as undefined | { name?: string };
    const responseName = responseData?.name;
    return {
      gameTitle:
        responseName && typeof responseName === "string" && responseName.includes("__")
          ? responseName.split("__")[0]
          : null,
      isValidGame: true,
    };
  } catch (error) {
    console.error("Error checking crossword availability:", error);
    return {
      isValidGame: false,
    };
  }
};

/**
 * Check a word search game date and checks if the game exists
 *
 * @param date - The date string to valid
 * @returns Boolean indicating if the game for the date is valid
 */
export const checkWordSearchDate = async (date: string): Promise<boolean> => {
  if (!date || typeof date !== "string") {
    return false;
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) {
    return false;
  }

  try {
    // Check if game exists for this date
    const response = await axios.get(
      `${config.game.api.host}/public/game-asset/scrambler_${date}`,
      {
        headers: {
          apiKey: config.game.api.apiKey,
        },
      },
    );

    if (response.status !== 200) {
      console.error(`Failed to fetch word search data for ${date}: status ${response.status}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error checking word search availability:", error);
    return false;
  }
};
